import React from 'react'

import Script from 'dangerous-html/react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { CarFront, PawPrint, Moon, AlertTriangle, Snowflake, Trash2, Plus, Eye, MapPin, Check } from 'lucide-react'

import Navigation from '../components/navigation'
import Features6 from '../components/features6'
import ContactFormEmbed from '../components/contact-form-embed'
import Footer from '../components/footer'
import './home.css'

const Home = (props) => {
  const { t } = useTranslation()

  return (
    <div className="home-container1">
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />
        <link
          rel="canonical"
          href="https://fabulous-graceful-cormorant-ol6tms.teleporthq.app/"
        />
      </Helmet>
      <Navigation></Navigation>
      <section className="hero-section">
        <div className="hero-media-bg">
          <img
            alt="Family crossing safe street"
            src="https://images.pexels.com/photos/8576222/pexels-photo-8576222.jpeg?auto=compress&amp;cs=tinysrgb&amp;w=1500"
            className="hero-bg-img"
          />
          <div className="hero-scrim-overlay"></div>
        </div>
        <div className="hero-container">
          <div className="hero-content-wrapper">
            <div className="hero-text-block">
              <h1 className="hero-title home-hero-title">
                {t('hero.title')}
              </h1>
              <p className="home-hero-subtitle hero-subtitle">
                <span>{t('hero.subtitle1')}</span>
                <br></br>
                <span>{t('hero.subtitle2')}</span>
                <br></br>
                <span>{t('hero.subtitle3')}</span>
              </p>
              <div className="hero-cta-group">
                <div className="hero-app-links">
                  <a href="https://apps.apple.com/jp/app/%E3%81%BE%E3%81%A1%E3%81%AE%E6%B0%97%E3%81%A5%E3%81%8D/id6759237613"
                     target="_blank"
                     rel="noreferrer noopener"
                  >
                    <div className="hero-app-btn btn-lg btn-primary btn">
                      <svg
                        fill="none"
                        width="24"
                        xmlns="http://www.w3.org/2000/svg"
                        height="24"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 19H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5.5"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      <span>{t('hero.appStore')}</span>
                    </div>
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=place.point.localnote"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <div className="home-thq-btn-elm2 hero-app-btn btn-lg btn-secondary btn">
                      <svg
                        fill="none"
                        width="24"
                        xmlns="http://www.w3.org/2000/svg"
                        height="24"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect
                          x="5"
                          y="2"
                          rx="2"
                          ry="2"
                          width="14"
                          height="20"
                        ></rect>
                        <path d="M12 18h.01"></path>
                      </svg>
                      <span>{t('hero.googlePlay')}</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
            <div className="hero-mockup-block">
              <div className="hero-phone-frame">
                <div className="hero-phone-screen">
                  <img
                    src="/phone-screen-img.png"
                    alt={t('hero.phoneAlt')}
                    className="hero-phone-screenshot"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="process-section">
        <div className="process-container">
          <div className="process-header">
            <h2 className="section-title">{t('process.title')}</h2>
            <p className="section-subtitle">{t('process.subtitle')}</p>
          </div>
          <div className="process-steps">
            <div className="process-step-item">
              <div className="process-phone-wrapper">
                <div className="process-phone-mini">
                  <div className="process-screen-map">
                    <div className="process-map-lines">
                      <div className="process-map-line"></div>
                      <div className="process-map-line process-map-line-v"></div>
                      <div className="process-map-line process-map-line-h2"></div>
                      <div className="process-map-line process-map-line-v2"></div>
                    </div>
                    <div className="process-fab">
                      <Plus size={22} strokeWidth={2.5} />
                    </div>
                    <div className="process-fab-ring"></div>
                  </div>
                </div>
                <div className="process-badge">
                  <span>1</span>
                </div>
              </div>
              <h3 className="process-step-title">{t('process.step1Title')}</h3>
              <p className="section-content">
                <span>{t('process.step1Desc1')}</span>
                <br></br>
                <span>{t('process.step1Desc2')}</span>
              </p>
            </div>
            <div className="process-step-item">
              <div className="process-phone-wrapper">
                <div className="process-phone-mini">
                  <div className="process-screen-categories">
                    <div className="process-cat-title">{t('process.catLabel')}</div>
                    <div className="process-cat-grid">
                      <div className="process-cat-chip">
                        <Eye size={14} />
                        <span>{t('process.catPoorVisibility')}</span>
                      </div>
                      <div className="process-cat-chip process-cat-chip-active">
                        <PawPrint size={14} />
                        <span>{t('process.catAnimals')}</span>
                      </div>
                      <div className="process-cat-chip">
                        <Moon size={14} />
                        <span>{t('process.catDark')}</span>
                      </div>
                      <div className="process-cat-chip process-cat-chip-active">
                        <CarFront size={14} />
                        <span>{t('process.catTraffic')}</span>
                      </div>
                      <div className="process-cat-chip">
                        <Snowflake size={14} />
                        <span>{t('process.catFrozen')}</span>
                      </div>
                      <div className="process-cat-chip">
                        <Trash2 size={14} />
                        <span>{t('process.catLittering')}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="process-badge">
                  <span>2</span>
                </div>
              </div>
              <h3 className="process-step-title">{t('process.step2Title')}</h3>
              <p className="section-content">{t('process.step2Desc')}</p>
            </div>
            <div className="process-step-item">
              <div className="process-phone-wrapper">
                <div className="process-phone-mini">
                  <div className="process-screen-done">
                    <div className="process-done-pin">
                      <MapPin size={36} />
                    </div>
                    <div className="process-done-check">
                      <Check size={16} strokeWidth={3} />
                    </div>
                    <div className="process-done-text">{t('process.doneText')}</div>
                  </div>
                </div>
                <div className="process-badge">
                  <span>3</span>
                </div>
              </div>
              <h3 className="process-step-title">{t('process.step3Title')}</h3>
              <p className="section-content">{t('process.step3Desc')}</p>
            </div>
          </div>
        </div>
      </section>
      <section className="showcase-section">
        <div className="showcase-content-grid">
          <div className="showcase-visual-block">
            <div className="showcase-map-container">
              <div className="showcase-map-overlay">
                <div className="showcase-cluster">
                  <div className="showcase-pin-pulse"></div>
                  <div className="showcase-agree-count">
                    <svg
                      fill="none"
                      width="16"
                      xmlns="http://www.w3.org/2000/svg"
                      height="16"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M7 10v12"></path>
                      <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2h0a3.13 3.13 0 0 1 3 3.88Z"></path>
                    </svg>
                    <span>{t('showcase.agreeCount')}</span>
                  </div>
                </div>
                <p className="showcase-pin-desc">
                  <span>{t('showcase.pinDesc1')}</span>
                  <br></br>
                  <span>{t('showcase.pinDesc2')}</span>
                </p>
              </div>
            </div>
          </div>
          <div className="showcase-text-block">
            <h2 className="section-title">{t('showcase.title')}</h2>
            <p className="section-content">{t('showcase.description')}</p>
            <ul className="showcase-feature-list">
              <li className="showcase-list-item">
                <svg
                  fill="none"
                  width="20"
                  xmlns="http://www.w3.org/2000/svg"
                  height="20"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle r="10" cx="12" cy="12"></circle>
                  <path d="m9 12 2 2 4-4"></path>
                </svg>
                <span>{t('showcase.point1')}</span>
              </li>
              <li className="showcase-list-item">
                <svg
                  fill="none"
                  width="20"
                  xmlns="http://www.w3.org/2000/svg"
                  height="20"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle r="10" cx="12" cy="12"></circle>
                  <path d="m9 12 2 2 4-4"></path>
                </svg>
                <span>{t('showcase.point2')}</span>
              </li>
              <li className="showcase-list-item">
                <svg
                  fill="none"
                  width="20"
                  xmlns="http://www.w3.org/2000/svg"
                  height="20"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle r="10" cx="12" cy="12"></circle>
                  <path d="m9 12 2 2 4-4"></path>
                </svg>
                <span>{t('showcase.point3')}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
      <section id="usecase" className="features-section">
        <div className="features-container">
          <div className="features-header">
            <h2 className="section-title">{t('usecase.title')}</h2>
            <p className="section-subtitle">{t('usecase.subtitle')}</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon-circle orange-tint">
                <CarFront size={24} />
              </div>
              <h3 className="feature-card-title">{t('usecase.card1Title')}</h3>
              <p className="section-content">{t('usecase.card1Desc')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-circle teal-tint">
                <PawPrint size={24} />
              </div>
              <h3 className="feature-card-title">{t('usecase.card2Title')}</h3>
              <p className="section-content">{t('usecase.card2Desc')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-circle orange-tint">
                <Moon size={24} />
              </div>
              <h3 className="feature-card-title">{t('usecase.card3Title')}</h3>
              <p className="section-content">{t('usecase.card3Desc')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-circle teal-tint">
                <AlertTriangle size={24} />
              </div>
              <h3 className="feature-card-title">{t('usecase.card4Title')}</h3>
              <p className="section-content">{t('usecase.card4Desc')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-circle orange-tint">
                <Snowflake size={24} />
              </div>
              <h3 className="feature-card-title">{t('usecase.card5Title')}</h3>
              <p className="section-content">{t('usecase.card5Desc')}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon-circle teal-tint">
                <Trash2 size={24} />
              </div>
              <h3 className="feature-card-title">{t('usecase.card6Title')}</h3>
              <p className="section-content">{t('usecase.card6Desc')}</p>
            </div>
          </div>
        </div>
      </section>
      <section id="feature" className="about-section">
        <div className="about-container">
          <div className="about-header">
            <h2 className="section-title">{t('about.title')}</h2>
            <p className="section-subtitle">{t('about.subtitle')}</p>
          </div>
          <div className="about-grid">
            <div className="about-item">
              <h3 className="about-item-title">{t('about.item1Title')}</h3>
              <p className="section-content">{t('about.item1Desc')}</p>
            </div>
            <div className="about-item">
              <h3 className="about-item-title">{t('about.item2Title')}</h3>
              <p className="section-content">{t('about.item2Desc')}</p>
            </div>
            <div className="about-item">
              <h3 className="about-item-title">{t('about.item3Title')}</h3>
              <p className="section-content">{t('about.item3Desc')}</p>
            </div>
            <div className="about-item">
              <h3 className="about-item-title">{t('about.item4Title')}</h3>
              <p className="section-content">{t('about.item4Desc')}</p>
            </div>
            <div className="about-item">
              <h3 className="about-item-title">{t('about.item5Title')}</h3>
              <p className="section-content">{t('about.item5Desc')}</p>
            </div>
            <div className="about-item">
              <h3 className="about-item-title">{t('about.item6Title')}</h3>
              <p className="section-content">{t('about.item6Desc')}</p>
            </div>
          </div>
        </div>
      </section>
      <Features6 />
      <section id="download" className="final-cta-section">
        <div className="cta-container">
          <div className="cta-grid">
            <div className="cta-text-block">
              <h2 className="section-title">{t('cta.title')}</h2>
              <p className="section-content">
                <span>{t('cta.desc1')}</span>
                <br></br>
                <span>{t('cta.desc2')}</span>
              </p>
            </div>
            <div className="home-thq-cta-buttons-column-elm">
              <div className="cta-buttons">
                <a href="https://apps.apple.com/jp/app/%E3%81%BE%E3%81%A1%E3%81%AE%E6%B0%97%E3%81%A5%E3%81%8D/id6759237613"
                   target="_blank"
                   rel="noreferrer noopener"
                >
                  <div className="home-thq-btn-elm3 btn-lg btn-primary btn">
                    <svg
                      fill="none"
                      width="20"
                      xmlns="http://www.w3.org/2000/svg"
                      height="20"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 19H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5.5"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    <span>{t('cta.appStore')}</span>
                  </div>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=place.point.localnote"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <div className="home-thq-btn-elm4 btn-lg btn-secondary btn">
                    <svg
                      fill="none"
                      width="20"
                      xmlns="http://www.w3.org/2000/svg"
                      height="20"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect
                        x="5"
                        y="2"
                        rx="2"
                        ry="2"
                        width="14"
                        height="20"
                      ></rect>
                      <path d="M12 18h.01"></path>
                    </svg>
                    <span>{t('cta.googlePlay')}</span>
                  </div>
                </a>
              </div>
              <div className="home-thq-cta-qr-block-elm cta-qr-block"></div>
            </div>
          </div>
        </div>
      </section>
      <ContactFormEmbed></ContactFormEmbed>
      <div className="home-container2">
        <div className="home-container3">
          <Script
            html={`<style>
        @keyframes pinDrop {0% {transform: translate(-50%, -200%);
opacity: 0;}
60% {transform: translate(-50%, -90%);
opacity: 1;}
100% {transform: translate(-50%, -100%);
opacity: 1;}}@keyframes pulse {0% {transform: translate(-50%, -50%) scale(1);
opacity: 0.8;}
100% {transform: translate(-50%, -50%) scale(3);
opacity: 0;}}
        </style> `}
          ></Script>
        </div>
      </div>
      <div className="home-container4">
        <div className="home-container5">
          <Script
            html={`<script defer data-name="app-interactivity">
(function(){
  // Simple script to handle small scroll-based entrance animations
  const observerOptions = {
    threshold: 0.1,
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.style.opacity = "1"
        entry.target.style.transform = "translateY(0)"
      }
    })
  }, observerOptions)

  // Target all major blocks for subtle entrance
  document.querySelectorAll(".feature-card, .impact-item, .process-step-item, .about-item").forEach((el) => {
    el.style.opacity = "0"
    el.style.transform = "translateY(20px)"
    el.style.transition = "all 0.6s ease-out"
    observer.observe(el)
  })

  // Mock pin interaction
  const pins = document.querySelectorAll(".hero-map-pin")
  pins.forEach((pin, index) => {
    pin.style.animationDelay = \`\${index * 0.3}s\`
  })
})()
</script>`}
          ></Script>
        </div>
      </div>
      <Footer></Footer>
    </div>
  )
}

export default Home
